import React from 'react';
import { ArrowRight, PlayCircle, Sparkles, Brain, ScanLine, HeartPulse } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { HERO } from '../data/mock';
import HeroAnimations from './HeroAnimations';
import CountUp from './CountUp';

// Extract numeric portion + suffix from stat values like "1,000+", "25+", "10+", "2"
function parseStat(str) {
  const m = String(str).match(/^([\d,]+)(.*)$/);
  if (!m) return { num: 0, suffix: str };
  return { num: parseInt(m[1].replace(/,/g, ''), 10), suffix: m[2] };
}

export default function Hero() {
  return (
    <section id="top" className="relative pt-[130px] pb-24 lg:pt-[150px] lg:pb-32 overflow-hidden">
      <HeroAnimations />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7 flex flex-col items-start">
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white/80 backdrop-blur px-3.5 py-1.5 text-[12.5px] font-medium text-[#B54A15] shadow-sm animate-[fadeUp_.8s_ease-out]">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF7A2E] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E85A18]" />
              </span>
              {HERO.eyebrow}
            </div>

            <h1
              className="mt-6 text-[42px] sm:text-[54px] lg:text-[74px] leading-[1.02] tracking-[-0.03em] text-[#0F1B2D] font-semibold animate-[fadeUp_1s_ease-out]"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {HERO.titleLead}{' '}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-[#FF7A2E] to-[#E85A18] bg-clip-text text-transparent">
                  {HERO.titleAccent}
                </span>
                <span className="absolute left-0 right-0 bottom-1 h-3 bg-orange-200/70 -z-0 rounded-sm" />
              </span>{' '}
              {HERO.titleMid}{' '}
              <span className="bg-gradient-to-r from-[#FF7A2E] to-[#E85A18] bg-clip-text text-transparent">
                {HERO.titleAccent2}
              </span>
            </h1>

            <p className="mt-7 max-w-2xl text-[17px] lg:text-[18.5px] leading-[1.65] text-slate-600 animate-[fadeUp_1.2s_ease-out]">
              {HERO.subtitle}
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4 animate-[fadeUp_1.4s_ease-out]">
              <Button
                asChild
                className="group h-12 px-6 rounded-full bg-[#0F1B2D] hover:bg-[#1a2942] text-white text-[14.5px] font-medium overflow-hidden relative"
              >
                <a href="#products">
                  <span className="relative z-10 inline-flex items-center">
                    {HERO.primaryCta}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                  <span
                    className="absolute inset-0 -translate-x-full group-hover:animate-[shine_1.1s_ease] bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    aria-hidden
                  />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="h-12 px-6 rounded-full border-slate-300 hover:border-[#FF7A2E] hover:text-[#E85A18] hover:bg-orange-50 text-slate-700 text-[14.5px] font-medium bg-white/70 backdrop-blur"
              >
                <Link to="/contact">
                  <PlayCircle className="mr-2 h-4 w-4" /> {HERO.secondaryCta}
                </Link>
              </Button>
            </div>

            {/* Stat strip with count-ups */}
            <div className="mt-14 w-full grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-4 border-t border-orange-100/80 pt-8 animate-[fadeUp_1.6s_ease-out]">
              {HERO.stats.map((s) => {
                const { num, suffix } = parseStat(s.value);
                return (
                  <div key={s.label} className="pr-4">
                    <div
                      className="text-[30px] md:text-[38px] font-semibold text-[#0F1B2D] tracking-tight"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      <CountUp to={num} suffix={suffix} />
                    </div>
                    <div className="text-[12.5px] uppercase tracking-[0.16em] text-slate-500 mt-1">
                      {s.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: 3D-ish medical visual dashboard */}
          <div className="lg:col-span-5 relative hidden lg:block">
            <MedicalVisualStack />
          </div>
        </div>
      </div>
    </section>
  );
}

// Stacked, animated "medical dashboard" visual for the hero.
// Broken into small sub-components to keep functions <50 lines.
function MedicalVisualStack() {
  return (
    <div className="relative w-full h-[520px] flex items-center justify-center" style={{ perspective: 1200 }}>
      <OrbitalRings />
      <RipplePulses />
      <CentralOrb />
      <OrbitingDots />
      <ScanCard />
      <NeuroCard />
      <FloatingHeartChip />
    </div>
  );
}

function OrbitalRings() {
  return (
    <>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-[440px] h-[440px] rounded-full border border-orange-200/70 animate-[spinSlow_36s_linear_infinite]" />
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-[340px] h-[340px] rounded-full border border-orange-200/50 animate-[spinSlow_22s_linear_infinite_reverse]" />
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-[240px] h-[240px] rounded-full border border-orange-200/35" />
      </div>
    </>
  );
}

function RipplePulses() {
  return (
    <>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-56 h-56 rounded-full bg-[radial-gradient(closest-side,#FF7A2E33,transparent_70%)] animate-[rippleRing_3s_ease-out_infinite]" />
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="w-56 h-56 rounded-full bg-[radial-gradient(closest-side,#FF7A2E33,transparent_70%)] animate-[rippleRing_3s_ease-out_infinite]"
          style={{ animationDelay: '1.5s' }}
        />
      </div>
    </>
  );
}

function CentralOrb() {
  return (
    <div
      className="relative w-56 h-56 rounded-full flex items-center justify-center"
      style={{
        background: 'radial-gradient(circle at 30% 25%, #FFD7B8 0%, #FF7A2E 45%, #C93E10 100%)',
        boxShadow:
          '0 30px 90px -20px rgba(232,90,24,0.55), inset 0 -18px 40px rgba(0,0,0,0.25), inset 0 18px 40px rgba(255,255,255,0.35)',
      }}
    >
      <div className="w-40 h-40 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-inner">
        <svg viewBox="0 0 100 100" className="w-32 h-32">
          <defs>
            <linearGradient id="hb" x1="0" y1="0" x2="100" y2="0">
              <stop offset="0%" stopColor="#FF7A2E" />
              <stop offset="100%" stopColor="#E85A18" />
            </linearGradient>
          </defs>
          <path
            d="M6 50 L28 50 L34 32 L44 68 L52 40 L60 58 L66 50 L94 50"
            stroke="url(#hb)"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <animate attributeName="stroke-dasharray" values="0 300;300 0" dur="3s" repeatCount="indefinite" />
          </path>
        </svg>
      </div>
    </div>
  );
}

function OrbitingDots() {
  return [0, 60, 120, 180, 240, 300].map((deg) => (
    <div
      key={`orbit-${deg}`}
      className="absolute top-1/2 left-1/2 w-2.5 h-2.5 rounded-full bg-[#FF7A2E] shadow-[0_0_14px_#FF7A2E]"
      style={{
        transform: `rotate(${deg}deg) translate(220px) rotate(-${deg}deg)`,
        animation: `pulseDot 2.4s ${(deg / 60) * 0.25}s ease-in-out infinite`,
      }}
    />
  ));
}

function ScanCard() {
  return (
    <div className="absolute top-2 -left-4 w-[220px] rounded-2xl bg-white/95 backdrop-blur border border-orange-100 p-4 shadow-[0_20px_50px_-20px_rgba(15,27,45,0.25)] animate-[floatY_6s_ease-in-out_infinite]">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
        <ScanLine className="h-3.5 w-3.5 text-[#E85A18]" /> Stroke Insightz
      </div>
      <div className="mt-2 text-[13px] font-semibold text-[#0F1B2D]">Perfusion analyzed</div>
      <div className="mt-2 flex items-end gap-2">
        <div className="text-[24px] font-semibold text-[#E85A18] leading-none" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          <CountUp to={27} suffix="s" />
        </div>
        <div className="text-[11px] text-slate-500 pb-0.5">core / penumbra</div>
      </div>
      <div className="mt-3 grid grid-cols-6 gap-1">
        {['a', 'b', 'c', 'd', 'e', 'f'].map((slot, i) => (
          <div key={`scan-bar-${slot}`} className="h-1.5 rounded-full bg-orange-100 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#FF7A2E] to-[#E85A18] animate-[shine_2.4s_ease_infinite]"
              style={{ animationDelay: `${i * 0.15}s`, width: '70%' }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function NeuroCard() {
  return (
    <div
      className="absolute bottom-4 -right-4 w-[230px] rounded-2xl bg-white/95 backdrop-blur border border-orange-100 p-4 shadow-[0_20px_50px_-20px_rgba(15,27,45,0.25)] animate-[floatY_7s_ease-in-out_infinite]"
      style={{ animationDelay: '1.2s' }}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
        <Brain className="h-3.5 w-3.5 text-[#E85A18]" /> RehabVeda R1
      </div>
      <div className="mt-2 text-[13px] font-semibold text-[#0F1B2D]">Motor-Imagery signal</div>
      <div className="mt-3">
        <svg viewBox="0 0 200 40" className="w-full h-8">
          <path
            d="M0 20 L20 20 L28 8 L36 30 L44 12 L52 26 L60 20 L200 20"
            stroke="#E85A18"
            strokeWidth="1.6"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="text-[11px] text-slate-500">Neuroplasticity index</div>
        <div className="text-[13px] font-semibold text-[#0F1B2D]">
          <CountUp to={87} suffix="%" />
        </div>
      </div>
    </div>
  );
}

function FloatingHeartChip() {
  return (
    <div
      className="absolute top-24 -right-6 h-12 w-12 rounded-2xl bg-gradient-to-br from-[#FFB27A] to-[#E85A18] flex items-center justify-center shadow-[0_20px_40px_-15px_rgba(232,90,24,0.55)] animate-[floatY_5s_ease-in-out_infinite]"
      style={{ animationDelay: '0.4s' }}
    >
      <HeartPulse className="h-6 w-6 text-white" />
    </div>
  );
}
